/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import { useCallback, useEffect, useLayoutEffect } from 'react'
import { useLazyQuery, useReactiveVar } from '@apollo/client'
import { GET_SMART_CONTRACTS } from 'api/general/get-smart-contracts'
import { SOLANA_NETWORK } from 'app/solana-config'
import { DEFAULT_CONTRACT_VERSION } from 'contracts/constants'
import { smartContractsVar } from 'shared/store/contracts'
import { globalOverlaySmartContractsLoadingVar } from 'shared/store/global-overlay-state-store'
import { notificationStateVar } from 'shared/store/notification'
import { Web3Adress } from 'shared/types'

type ContractEntryName =
  | 'OneVsOne'
  | 'UpDown'
  | 'UpDownAi'
  | 'FrontHelper'
  | 'Dataverifier'
  | 'Bullseye'
  | 'USDC'
  | 'Treasury'
  | 'Setup'
  | 'Race'

const chainIdentifier = `solana:${SOLANA_NETWORK}` as const

export const useSmartContracts = () => {
  const [getSmartContracts, { loading }] = useLazyQuery(GET_SMART_CONTRACTS)

  const handleGetSmartContracts = useCallback(async () => {
    try {
      const res = await getSmartContracts()

      const smartContractEntries = res.data?.getSmartContracts || null

      if (!smartContractEntries) return

      smartContractsVar(smartContractEntries)
    } catch {
      notificationStateVar({
        isOpen: true,
        type: 'error',
        title: 'Error! Smart contract addresses were not fetched'
      })
    }
  }, [getSmartContracts])

  useEffect(() => {
    globalOverlaySmartContractsLoadingVar(loading)
  }, [loading])

  useLayoutEffect(() => {
    handleGetSmartContracts()
  }, [handleGetSmartContracts])
}

export const useGetSmartContract = (contractType: ContractEntryName) => {
  const smartContracts = useReactiveVar(smartContractsVar)

  const smartContractEntry = smartContracts.find(
    c => c.contractName === contractType
  )

  const chainFilter = (c: { chainType: string; chainIdentifier: string }) =>
    c.chainType === 'solana' && c.chainIdentifier === chainIdentifier

  const highestContractsVersion =
    [...(smartContractEntry?.contracts || [])]
      ?.filter(chainFilter)
      ?.sort((a, b) => b?.version - a?.version)?.[0]?.version ||
    DEFAULT_CONTRACT_VERSION

  const smartContractAddress = (smartContractEntry?.contracts
    ?.filter(chainFilter)
    ?.find(c => c?.version === highestContractsVersion)?.contractAddress ||
    smartContractEntry?.contracts?.[0]?.contractAddress ||
    '') as Web3Adress

  const smartContractVersion =
    smartContractEntry?.contracts
      ?.filter(chainFilter)
      ?.find(c => c?.version === highestContractsVersion)?.version ||
    smartContractEntry?.contracts?.[0]?.version ||
    DEFAULT_CONTRACT_VERSION

  const getContractVersionByAddress = (address: Web3Adress) => {
    return (
      smartContractEntry?.contracts?.find(c => c?.contractAddress === address)
        ?.version ||
      smartContractEntry?.contracts?.[0]?.version ||
      DEFAULT_CONTRACT_VERSION
    )
  }

  const getContractEntryByAddress = (address: Web3Adress) => {
    const contractEntry = smartContracts.find(c =>
      c.contracts?.some(contract => contract.contractAddress === address)
    )

    const contractEntity = contractEntry?.contracts?.find(
      c => c.contractAddress === address
    )

    return contractEntity
  }

  return {
    smartContractEntry,
    smartContractAddress,
    smartContractVersion,
    getContractVersionByAddress,
    getContractEntryByAddress
  }
}
